import { z } from 'zod';
import { compareDecimal, absDecimal } from '../utils/decimal.js';
import { idSchema, moneySchema, percentSchema, isoDateTimeSchema } from './common.js';
import { ledgerDateSchema } from './financial-accounting.js';
import { SUPPORTED_LOCALES } from '../constants/app.js';
export const vatReturnClassificationSchema = z.enum([
    'DOMESTIC',
    'EU_SUPPLY',
    'EXEMPT_WITH_DEDUCTION',
    'EXEMPT_WITHOUT_DEDUCTION',
    'OUTGOING_REVERSE_CHARGE',
    'EU_SERVICE_SUPPLY',
    'FOREIGN_PLACE_SUPPLY',
    'EU_ACQUISITION',
    'EU_ACQUISITION_EXEMPT',
    'RC_EU_SERVICE',
    'RC_PROPERTY',
    'RC_OTHER',
    'IMPORT_VAT',
    'NO_GERMAN_VAT',
]);
export const vatReturnPeriodSchema = z
    .object({ dateFrom: ledgerDateSchema, dateTo: ledgerDateSchema })
    .strict()
    .superRefine((value, context) => {
    if (value.dateFrom > value.dateTo)
        context.addIssue({ code: 'custom', path: ['dateTo'], message: 'vatReturn.errors.dateRange' });
    if (!value.dateFrom.startsWith('2026-') || !value.dateTo.startsWith('2026-'))
        context.addIssue({ code: 'custom', path: ['dateFrom'], message: 'vatReturn.errors.year' });
});
const vatReturnAmountsSchema = z.object({
    flow: z.enum(['SALE', 'PURCHASE']),
    classification: vatReturnClassificationSchema.nullable(),
    rate: percentSchema,
    net: moneySchema,
    legalTax: moneySchema,
    deductibleTax: moneySchema,
});
function refineVatAmounts(value, context) {
    const issue = (path) => context.addIssue({ code: 'custom', path: [path], message: 'vatReturn.errors.source' });
    if (compareDecimal(absDecimal(value.deductibleTax), absDecimal(value.legalTax)) > 0 ||
        (compareDecimal(value.legalTax, '0') < 0 && compareDecimal(value.deductibleTax, '0') > 0) ||
        (compareDecimal(value.legalTax, '0') > 0 && compareDecimal(value.deductibleTax, '0') < 0) ||
        (value.flow === 'SALE' && compareDecimal(value.deductibleTax, '0') !== 0))
        issue('deductibleTax');
    if (compareDecimal(value.rate, '0') < 0 || compareDecimal(value.rate, '100') > 0)
        issue('rate');
    if (value.classification !== 'IMPORT_VAT' &&
        compareDecimal(value.rate, '0') === 0 &&
        compareDecimal(value.legalTax, '0') !== 0)
        issue('legalTax');
    const purchases = [
        'EU_ACQUISITION',
        'EU_ACQUISITION_EXEMPT',
        'RC_EU_SERVICE',
        'RC_PROPERTY',
        'RC_OTHER',
        'IMPORT_VAT',
        'NO_GERMAN_VAT',
    ];
    if (value.classification !== null && value.classification !== 'DOMESTIC') {
        if (purchases.includes(value.classification) !== (value.flow === 'PURCHASE'))
            issue('classification');
        if (!['EU_ACQUISITION', 'RC_EU_SERVICE', 'RC_PROPERTY', 'RC_OTHER', 'IMPORT_VAT'].includes(value.classification) &&
            (compareDecimal(value.legalTax, '0') !== 0 || compareDecimal(value.deductibleTax, '0') !== 0))
            issue('legalTax');
    }
}
/** An adapter must reconcile these source facts with the posted journals before using them. */
export const vatReturnEventSchema = vatReturnAmountsSchema
    .extend({
    id: z.string().min(1).max(200),
    journalIds: z.array(idSchema).min(1).max(100),
    date: ledgerDateSchema,
    currency: z.literal('EUR'),
    ledgerOutputTax: moneySchema,
    ledgerInputTax: moneySchema,
})
    .strict()
    .superRefine((value, context) => {
    refineVatAmounts(value, context);
    if (new Set(value.journalIds).size !== value.journalIds.length)
        context.addIssue({
            code: 'custom',
            path: ['journalIds'],
            message: 'vatReturn.errors.source',
        });
});
export const vatReturnReviewBucketSchema = vatReturnAmountsSchema
    .extend({
    classification: vatReturnClassificationSchema,
})
    .strict()
    .superRefine(refineVatAmounts);
export const vatReturnReviewInputSchema = z
    .object({
    journalId: idSchema,
    reason: z.string().trim().min(20).max(2000),
    buckets: z.array(vatReturnReviewBucketSchema).min(1).max(500),
})
    .strict();
export const vatReturnReviewPreviewSchema = vatReturnReviewInputSchema.extend({
    sourceHash: z.string().regex(/^[a-f0-9]{64}$/u),
    date: ledgerDateSchema,
    ledgerOutputTax: moneySchema,
    ledgerInputTax: moneySchema,
    version: z.number().int().positive(),
    supersedesId: idSchema.nullable(),
});
export const vatReturnReviewCreateSchema = vatReturnReviewInputSchema.extend({
    sourceHash: z.string().regex(/^[a-f0-9]{64}$/u),
    version: z.number().int().positive(),
    idempotencyKey: z.string().trim().min(8).max(160),
});
export const vatReturnReviewSchema = vatReturnReviewPreviewSchema.extend({
    id: idSchema,
    reviewedAt: isoDateTimeSchema,
    reviewedById: idSchema,
});
export const vatReturnQuerySchema = z
    .object({
    dateFrom: ledgerDateSchema,
    dateTo: ledgerDateSchema,
    compareFrom: ledgerDateSchema.optional(),
    compareTo: ledgerDateSchema.optional(),
    q: z.string().trim().max(160).default(''),
    page: z.coerce.number().int().positive().default(1),
    pageSize: z.coerce.number().int().min(1).max(200).default(25),
    flow: z.enum(['SALE', 'PURCHASE']).optional(),
    issuesOnly: z
        .enum(['true', 'false'])
        .default('false')
        .transform((value) => value === 'true'),
    sort: z.enum(['date:asc', 'date:desc', 'number:asc', 'number:desc']).default('date:desc'),
})
    .superRefine((value, context) => {
    // Period validation is kept separate from table filters.
    const period = vatReturnPeriodSchema.safeParse({
        dateFrom: value.dateFrom,
        dateTo: value.dateTo,
    });
    if (!period.success)
        for (const issue of period.error.issues)
            context.addIssue(issue);
    if ((value.compareFrom === undefined) !== (value.compareTo === undefined)) {
        context.addIssue({
            code: 'custom',
            path: [value.compareFrom === undefined ? 'compareFrom' : 'compareTo'],
            message: 'vatReturn.errors.comparison',
        });
    }
    else if (value.compareFrom !== undefined && value.compareTo !== undefined) {
        const comparison = vatReturnPeriodSchema.safeParse({
            dateFrom: value.compareFrom,
            dateTo: value.compareTo,
        });
        if (!comparison.success)
            for (const issue of comparison.error.issues)
                context.addIssue({
                    ...issue,
                    path: [issue.path[0] === 'dateTo' ? 'compareTo' : 'compareFrom'],
                });
    }
});
export const vatReturnIssueCodeSchema = z.enum([
    'CLASSIFICATION_REQUIRED',
    'LEDGER_MISMATCH',
    'SOURCE_REVIEW_REQUIRED',
    'FISCAL_RATE_REQUIRED',
]);
export const vatReturnExportQuerySchema = vatReturnQuerySchema.and(z.object({
    locale: z.enum(SUPPORTED_LOCALES).default('de'),
}));
export const vatReturnSourceRowSchema = z.object({
    id: z.string().min(1),
    journalId: idSchema,
    journalNumber: z.number().int().nullable(),
    documentId: idSchema.nullable(),
    vendorInvoiceId: idSchema.nullable(),
    number: z.string(),
    description: z.string(),
    sourceType: z.string(),
    date: ledgerDateSchema,
    event: vatReturnEventSchema.nullable(),
    reviewId: idSchema.nullable(),
    issues: z.array(vatReturnIssueCodeSchema),
});
export const vatReturnBoxSchema = z.object({
    key: z.string().regex(/^\d{2}$/u),
    kind: z.enum(['BASE', 'OUTPUT_TAX', 'INPUT_TAX']),
    amount: moneySchema,
    exactAmount: moneySchema,
    taxAmount: moneySchema,
    eventIds: z.array(z.string()),
});
export const vatReturnPeriodReportSchema = z.object({
    year: z.literal(2026),
    dateFrom: ledgerDateSchema,
    dateTo: ledgerDateSchema,
    ready: z.boolean(),
    currency: z.literal('EUR'),
    boxes: z.array(vatReturnBoxSchema),
    ledgerOutputTax: moneySchema,
    ledgerInputTax: moneySchema,
    declaredOutputTax: moneySchema,
    declaredInputTax: moneySchema,
    payable: moneySchema,
    roundingDifference: moneySchema.nullable(),
    issueCount: z.number().int().nonnegative(),
    data: z.array(vatReturnSourceRowSchema),
    meta: z.object({
        page: z.number().int().positive(),
        pageSize: z.number().int().positive(),
        total: z.number().int().nonnegative(),
        totalPages: z.number().int().nonnegative(),
    }),
});
export const vatReturnReportSchema = vatReturnPeriodReportSchema.extend({
    comparison: vatReturnPeriodReportSchema.nullable().optional(),
});
//# sourceMappingURL=vat-return.js.map